import { useState } from 'react';
import { LocaleSwitcher } from 'components';
import LocationAwareSearch from 'components/LocationAwareSearch';
import { useTranslation } from 'react-i18next';
import {
  formatOrderCriteria,
  initialLocale,
  settingRequestState,
  stateEnum,
} from 'utils';
import { isEqual } from 'lodash';
import { useSearchParams, useApp } from 'hooks';
import { Stack } from '@koob/margaret';
import SettingRequestsByStateAndTheme from 'components/SettingRequest/SettingRequestsListByState';
import {
  KoobCreateButton,
  KoobDataTable,
  KoobInputSegmented,
  KoobListPageLayout,
  KoobPageHeader,
  koobTableUrlSort,
  KoobSettingsBody,
} from '@koob/ui';
import { getThemes } from 'api/node/themes';
import { useAsync } from 'react-async';
import { PER_PAGE_FOR_LIST } from 'constants';
import { useSettingsArea } from 'components/settingsAreas';

const ThemesList = () => {
  const { t } = useTranslation('experienceTheme');
  const area = useSettingsArea('experiences');
  const { user, locales } = useApp();

  const [searchParams, setSearchParams] = useSearchParams();
  const { search, state } = searchParams;
  const page = Number(searchParams.page) || 1;
  const perPage = Number(searchParams.perPage) || PER_PAGE_FOR_LIST;
  const setPage = (page) => setSearchParams({ ...searchParams, page });
  const setPerPage = (perPage) =>
    setSearchParams({ ...searchParams, perPage, page: undefined });
  const { sort, onSortChange } = koobTableUrlSort(
    searchParams,
    setSearchParams,
  );
  const [totalPages, setTotalPages] = useState();

  const currentLocale =
    (locales ?? []).find((locale) => locale.value === searchParams.locale) ??
    initialLocale;
  const setCurrentLocale = (locale) =>
    setSearchParams({
      ...searchParams,
      locale: locale?.value,
      page: undefined,
    });

  const filters = {
    search,
    locale: currentLocale?.value,
    page: page,
    perPage: perPage,
    order: formatOrderCriteria(searchParams).column.toLowerCase(),
    direction: formatOrderCriteria(searchParams).direction.toLowerCase(),
    discarded: state === stateEnum.ARCHIVED,
  };

  const { data: themes, isLoading } = useAsync({
    promiseFn: getThemes,
    filters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
    onResolve: (response) => {
      setTotalPages(response?.headers?.xTotalPages);
    },
  });
  const handleStateChange = (newState) => {
    setSearchParams({
      ...searchParams,
      search: undefined,
      state: newState,
      page: undefined,
    });
  };

  const stateOptions = [
    { value: null, label: t('misc:active') },
    { value: stateEnum.ARCHIVED, label: t('misc:archived') },
    { value: stateEnum.PENDING, label: t('misc:pending') },
    { value: stateEnum.REFUSED, label: t('misc:refused') },
  ];

  const totalCount = Number(themes?.headers?.xTotalCount || 0);

  const columns = [
    {
      key: 'enDisplayName',
      label: (
        <Stack gutterSize={0.375} alignY="baseline">
          <span>{t('name')}</span>
          <span className={`flag-icon flag-icon-gb`} />
        </Stack>
      ),
      sortable: true,
    },
    {
      key: 'displayName',
      label: (
        <Stack gutterSize={0.375} alignY="baseline">
          <span>{t('name')}</span>
          <span className={`flag-icon flag-icon-${currentLocale.alpha2}`} />
        </Stack>
      ),
      sortable: true,
    },
  ];

  return (
    <>
      <KoobPageHeader
        title={area.title}
        tabs={area.tabs}
        subtitle={
          themes && t('misc:resultsCount', { count: Number(totalCount) })
        }
        actions={
          user?.role === 'dmc_admin' && (
            <KoobCreateButton relative="path" to="../setting-request/new/theme">
              {t('newSettingRequest')}
            </KoobCreateButton>
          )
        }
      />
      <KoobSettingsBody>
        <KoobListPageLayout
          search={<LocationAwareSearch name="search" />}
          filters={
            <KoobInputSegmented
              options={stateOptions}
              value={state ?? null}
              onChange={handleStateChange}
            />
          }
          toolbarActions={
            <LocaleSwitcher
              queryLocale={currentLocale}
              updateQueryLocale={setCurrentLocale}
            />
          }
        >
          {settingRequestState.includes(state) ? (
            <SettingRequestsByStateAndTheme
              settingName={'theme'}
              kind={'experience'}
              state={state}
            />
          ) : (
            <div>
              <KoobDataTable
                columns={columns}
                data={themes?.data ?? []}
                loading={isLoading}
                sort={sort}
                onSortChange={onSortChange}
                pagination={{
                  page,
                  setPage,
                  totalPages,
                  totalCount,
                  perPage,
                  setPerPage,
                  block: true,
                }}
              />
            </div>
          )}
        </KoobListPageLayout>
      </KoobSettingsBody>
    </>
  );
};

export default ThemesList;
